// Third party
import { createFeatureSelector, createSelector } from '@ngrx/store';

// Local library
import { TError } from '@amwager/util-ts';

// Local file
import { AUTH_FEATURE_KEY } from './auth.reducer';
import {
  IAuthState,
  IForgotPassword,
  ILogin,
  ILogout,
  IRegister,
  ICompleteRegistration,
  IDocumentsVerification,
  IUser,
  IUpdatePassword,
  IJurisdiction
} from './auth.models';



// Lookup the 'Auth' feature state managed by NgRx
export const selectAuthState = createFeatureSelector<IAuthState>(
  AUTH_FEATURE_KEY
);

export const getAuthLoaded = createSelector(
  selectAuthState,
  (state: IAuthState): boolean | undefined => state?.isLoaded
);

export const getAuthError = createSelector(
  selectAuthState,
  (state: IAuthState): TError => state?.error
);


/**
 * Login
 */
export const getLogin = createSelector(
  selectAuthState,
  (state: IAuthState): ILogin | undefined => state?.login
);

export const getIsLoggingIn = createSelector(
  getLogin,
  (login: ILogin | undefined): boolean | undefined => login?.isLoggingIn || false
);

// export const getIsLoginSuccess = createSelector(
//   getLogin,
//   (login: ILogin | undefined): boolean | undefined => login?.isSuccessLogin
// );

export const getIsLoginSuccessResponse = createSelector(
  getLogin,
  (login: ILogin | undefined): boolean | undefined => login?.response
);

export const getIsLoginSuccess = createSelector(
  getLogin,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (login: ILogin | undefined): {response: any, isSuccessLogin: boolean | undefined} => ({
    response: login?.response,
    isSuccessLogin: login?.isSuccessLogin
  })
);

// export const getIsLoginFailed = createSelector(
//   state,
//   (state: IAuthState) => state?.error
// );

export const getIsAuthenticated = createSelector(
  selectAuthState,
  (state: IAuthState): boolean | undefined => state?.isAuthenticated
);

export const getIsRememberMe = createSelector(
  getLogin,
  (login: ILogin | undefined): boolean | undefined => login?.isRememberMe
);

/**
 * User
 */
export const getUser = createSelector(
  selectAuthState,
  (state: IAuthState): IUser | undefined => state?.user
);

export const getUserFullName = createSelector(
  getUser,
  (user: IUser | undefined): string | undefined => user?.isFullName
);

/**
 * Logout
 */
export const getLogout = createSelector(
  selectAuthState,
  (state: IAuthState): ILogout | undefined => state?.logout
);

export const getIsLoggingOut = createSelector(
  getLogout,
  (logout: ILogout | undefined): boolean | undefined => logout?.isLoggingOut
);

export const getIsLogoutSuccess = createSelector(
  getLogout,
  (logout: ILogout | undefined): boolean | undefined => logout?.isSuccessLogout
);

/**
 * Update Password
 */
export const getUpdatePassowrd = createSelector(
  selectAuthState,
  (state: IAuthState): IUpdatePassword | undefined => state?.updatePassword
);

export const getIsUpdatingPassword = createSelector(
  getUpdatePassowrd,
  (updatePassword: IUpdatePassword | undefined):
  boolean | undefined => updatePassword?.isUpdatingPassword
);

export const getIsUpdatePasswordSuccess = createSelector(
  getUpdatePassowrd,
  (updatePassword: IUpdatePassword | undefined):
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {response: any, isSuccessUpdatePassword: boolean | undefined} => ({
    response: updatePassword?.response,
    isSuccessUpdatePassword: updatePassword?.isSuccessUpdatePassword
  })
);

// export const getIsUpdatePasswordFailed = createSelector(
//   state,
//   (state: IAuthState) => state?.error
// );

/**
 * Register
 */
export const getRegister = createSelector(
  selectAuthState,
  (state: IAuthState): IRegister | undefined => state?.register
);

export const getIsRegistering = createSelector(
  getRegister,
  (register: IRegister | undefined): boolean | undefined => register?.isRegistering
);

export const getIsRegisterSuccess = createSelector(
  getRegister,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (register: IRegister | undefined): {response: any, isSuccessRegister: boolean | undefined} => ({
    response: register?.response,
    isSuccessRegister: register?.isSuccessRegister
  })
);

// export const getIsRegisterFailed = createSelector(
//   state,
//   (state: IAuthState) => state?.error
// );

/**
 * Complete Registration
 */
export const getCompleteRegistration = createSelector(
  selectAuthState,
  (state: IAuthState): ICompleteRegistration | undefined => state?.completeRegistration
);

export const getIsCompleteRegistering = createSelector(
  getCompleteRegistration,
  (completeRegistration: ICompleteRegistration | undefined):
  boolean | undefined => completeRegistration?.isCompleteRegistering
);

export const getIsCompleteRegistrationSuccess = createSelector(
  getCompleteRegistration,
  (completeRegistration: ICompleteRegistration | undefined):
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {response: any, isSuccessCompleteRegistration: boolean | undefined} => ({
      response: completeRegistration?.response,
      isSuccessCompleteRegistration: completeRegistration?.isSuccessCompleteRegistration
  })
);

/**
 * Documents Verification
 */
export const getDocumentsVerification = createSelector(
  selectAuthState,
  (state: IAuthState): IDocumentsVerification | undefined => state?.documentsVerification
);

export const getIsDocumentsVerificating = createSelector(
  getDocumentsVerification,
  (documentsVerification: IDocumentsVerification | undefined):
    boolean | undefined => documentsVerification?.isDocumentsVerificating
);

export const getIsDocumentsVerificationSuccess = createSelector(
  getDocumentsVerification,
  (documentsVerification: IDocumentsVerification | undefined):
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {response: any, isSuccessDocumentsVerification: boolean | undefined} => ({
      response: documentsVerification?.response,
      isSuccessDocumentsVerification: documentsVerification?.isSuccessDocumentsVerification
  })
);

export const getIdFile = createSelector(
  getDocumentsVerification,

  (documentsVerification: IDocumentsVerification | undefined)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  : any => documentsVerification?.idFile
);

export const getBillFIle = createSelector(
  getDocumentsVerification,
  (documentsVerification: IDocumentsVerification | undefined)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  : any => documentsVerification?.billFile
);

/**
 * Forgot Password
 */
export const getRenewPassword = createSelector(
  selectAuthState,
  (state: IAuthState): IForgotPassword | undefined => state?.forgotPassword
);

export const getIsRenewingPassword = createSelector(
  getRenewPassword,
  (
    forgotPassword: IForgotPassword | undefined
  ): boolean | undefined => forgotPassword?.isRenewingPassword || false
);

export const getIsRenewPasswordSuccess = createSelector(
  getRenewPassword,
  (forgotPassword: IForgotPassword | undefined):
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {response: any, isSuccessRenewPassword: boolean | undefined} => ({
    response: forgotPassword?.response,
    isSuccessRenewPassword: forgotPassword?.isSuccessRenewPassword
  })
);

// export const getIsRenewPasswordFailed = createSelector(
//   state,
//   (state: IAuthState) => state?.error
// );

/**
 * Jurisdictions
 */
export const getJurisdictions = createSelector(
  selectAuthState,
  (state: IAuthState): IJurisdiction | undefined => state?.jurisdictions
);

/**
 * Country States
 * using response as param
 *
 */
export const getCountryStates = createSelector(
  getJurisdictions,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (jurisdictions: IJurisdiction | undefined):
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {response: any} => ({
    // response: jurisdictions?.subdivisions
    response: jurisdictions?.response
  })
);

// export const getCountryStates = createSelector(
//   getJurisdictions,
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   (jurisdictions: IJurisdiction | undefined): any | undefined => jurisdictions?.subdivisions
// );
